import { openDB } from 'idb';

const DB_NAME = 'sbs-powerlifting';
const DB_VERSION = 3;

let dbPromise;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (!db.objectStoreNames.contains('logbook')) {
          const logStore = db.createObjectStore('logbook', { keyPath: 'id', autoIncrement: true });
          logStore.createIndex('date', 'date');
          logStore.createIndex('lift', 'lift');
          logStore.createIndex('programId', 'programId');
        }
        if (!db.objectStoreNames.contains('bodyweight')) {
          const bwStore = db.createObjectStore('bodyweight', { keyPath: 'id', autoIncrement: true });
          bwStore.createIndex('date', 'date');
        }
        if (!db.objectStoreNames.contains('prs')) {
          const prStore = db.createObjectStore('prs', { keyPath: 'id', autoIncrement: true });
          prStore.createIndex('lift', 'lift');
          prStore.createIndex('date', 'date');
        }
        if (!db.objectStoreNames.contains('programProgress')) {
          db.createObjectStore('programProgress', { keyPath: 'lift' });
        }

        // v3: Redesigned accessory stores
        // Delete old v2 stores if upgrading
        if (oldVersion >= 2) {
          if (db.objectStoreNames.contains('accessoryExercises')) {
            db.deleteObjectStore('accessoryExercises');
          }
          if (db.objectStoreNames.contains('accessoryLogs')) {
            db.deleteObjectStore('accessoryLogs');
          }
        }

        if (!db.objectStoreNames.contains('accessoryExercises')) {
          const aeStore = db.createObjectStore('accessoryExercises', { keyPath: 'id', autoIncrement: true });
          aeStore.createIndex('libraryId', 'libraryId');
          aeStore.createIndex('active', 'active');
        }
        if (!db.objectStoreNames.contains('accessoryLogs')) {
          const alStore = db.createObjectStore('accessoryLogs', { keyPath: 'id', autoIncrement: true });
          alStore.createIndex('date', 'date');
          alStore.createIndex('exerciseId', 'exerciseId');
        }
      },
    });
  }
  return dbPromise;
}

// === Logbook ===
export async function addLogEntry(entry) {
  const db = await getDB();
  return db.add('logbook', { ...entry, timestamp: Date.now() });
}

export async function getAllLogEntries() {
  const db = await getDB();
  return db.getAllFromIndex('logbook', 'date');
}

export async function getLogEntriesByLift(lift) {
  const db = await getDB();
  return db.getAllFromIndex('logbook', 'lift', lift);
}

export async function getLogEntry(id) {
  const db = await getDB();
  return db.get('logbook', id);
}

export async function updateLogEntry(entry) {
  const db = await getDB();
  return db.put('logbook', entry);
}

export async function deleteLogEntry(id) {
  const db = await getDB();
  return db.delete('logbook', id);
}

// === Bodyweight ===
export async function addBodyweight(weight) {
  const db = await getDB();
  return db.add('bodyweight', {
    weight,
    date: new Date().toISOString().split('T')[0],
    timestamp: Date.now(),
  });
}

export async function getAllBodyweights() {
  const db = await getDB();
  return db.getAllFromIndex('bodyweight', 'date');
}

export async function getLatestBodyweight() {
  const db = await getDB();
  const all = await db.getAllFromIndex('bodyweight', 'date');
  return all.length > 0 ? all[all.length - 1] : null;
}

export async function deleteBodyweight(id) {
  const db = await getDB();
  return db.delete('bodyweight', id);
}

// === Personal Records ===
export async function addPR(pr) {
  const db = await getDB();
  return db.add('prs', { ...pr, timestamp: Date.now() });
}

export async function getPRsByLift(lift) {
  const db = await getDB();
  return db.getAllFromIndex('prs', 'lift', lift);
}

export async function getAllPRs() {
  const db = await getDB();
  return db.getAll('prs');
}

export async function deletePR(id) {
  const db = await getDB();
  return db.delete('prs', id);
}

// === Program Progress ===
export async function getProgramProgress(lift) {
  const db = await getDB();
  return db.get('programProgress', lift);
}

export async function setProgramProgress(lift, progress) {
  const db = await getDB();
  return db.put('programProgress', { lift, ...progress });
}

export async function getAllProgramProgress() {
  const db = await getDB();
  return db.getAll('programProgress');
}

// === Accessory Exercises ===
// Schema: { id, libraryId?, name, primaryGroup, primarySub, notes, active, createdAt }

export async function addAccessoryExercise(exercise) {
  const db = await getDB();
  return db.add('accessoryExercises', {
    ...exercise,
    active: exercise.active !== undefined ? exercise.active : true,
    notes: exercise.notes || '',
    createdAt: Date.now(),
  });
}

export async function updateAccessoryExercise(exercise) {
  const db = await getDB();
  return db.put('accessoryExercises', exercise);
}

export async function getAllAccessoryExercises() {
  const db = await getDB();
  return db.getAll('accessoryExercises');
}

export async function getActiveAccessoryExercises() {
  const db = await getDB();
  const all = await db.getAll('accessoryExercises');
  return all.filter(e => e.active);
}

export async function getAccessoryExerciseByLibraryId(libraryId) {
  const db = await getDB();
  return db.getFromIndex('accessoryExercises', 'libraryId', libraryId);
}

export async function deleteAccessoryExercise(id) {
  const db = await getDB();
  return db.delete('accessoryExercises', id);
}

// === Accessory Logs ===
export async function addAccessoryLog(log) {
  const db = await getDB();
  return db.add('accessoryLogs', {
    ...log,
    date: log.date || new Date().toISOString().split('T')[0],
    timestamp: Date.now(),
  });
}

export async function getAccessoryLogsByExercise(exerciseId) {
  const db = await getDB();
  return db.getAllFromIndex('accessoryLogs', 'exerciseId', exerciseId);
}

export async function getAllAccessoryLogs() {
  const db = await getDB();
  return db.getAllFromIndex('accessoryLogs', 'date');
}

export async function deleteAccessoryLog(id) {
  const db = await getDB();
  return db.delete('accessoryLogs', id);
}

// === Data Export ===
export async function exportAllData() {
  const db = await getDB();
  const data = {
    version: DB_VERSION,
    exportDate: new Date().toISOString(),
    appVersion: '1.0',
    indexedDB: {
      logbook: await db.getAll('logbook'),
      bodyweight: await db.getAll('bodyweight'),
      prs: await db.getAll('prs'),
      programProgress: await db.getAll('programProgress'),
      accessoryExercises: await db.getAll('accessoryExercises'),
      accessoryLogs: await db.getAll('accessoryLogs'),
    },
    localStorage: {},
  };
  const settingsRaw = localStorage.getItem('sbs_settings');
  if (settingsRaw) data.localStorage.sbs_settings = JSON.parse(settingsRaw);
  const customRaw = localStorage.getItem('sbs_custom_programs');
  if (customRaw) data.localStorage.sbs_custom_programs = JSON.parse(customRaw);
  return data;
}

// === Data Import (Replace All) ===
export async function importAllData(data) {
  if (!data || !data.indexedDB) throw new Error('Invalid backup file: missing data');
  const db = await getDB();
  const storeNames = ['logbook', 'bodyweight', 'prs', 'programProgress', 'accessoryExercises', 'accessoryLogs'];
  for (const storeName of storeNames) {
    const tx = db.transaction(storeName, 'readwrite');
    await tx.store.clear();
    await tx.done;
  }
  for (const [storeName, records] of Object.entries(data.indexedDB)) {
    if (!records || !Array.isArray(records) || records.length === 0) continue;
    const tx = db.transaction(storeName, 'readwrite');
    for (const record of records) { await tx.store.put(record); }
    await tx.done;
  }
  if (data.localStorage?.sbs_settings) {
    localStorage.setItem('sbs_settings', JSON.stringify(data.localStorage.sbs_settings));
  }
  if (data.localStorage?.sbs_custom_programs) {
    localStorage.setItem('sbs_custom_programs', JSON.stringify(data.localStorage.sbs_custom_programs));
  }
  return {
    logbook: data.indexedDB.logbook?.length || 0,
    bodyweight: data.indexedDB.bodyweight?.length || 0,
    prs: data.indexedDB.prs?.length || 0,
    programProgress: data.indexedDB.programProgress?.length || 0,
    accessoryExercises: data.indexedDB.accessoryExercises?.length || 0,
    accessoryLogs: data.indexedDB.accessoryLogs?.length || 0,
  };
}

export function previewBackup(data) {
  if (!data || !data.indexedDB) return null;
  return {
    exportDate: data.exportDate || 'Unknown',
    logbook: data.indexedDB.logbook?.length || 0,
    bodyweight: data.indexedDB.bodyweight?.length || 0,
    prs: data.indexedDB.prs?.length || 0,
    programProgress: data.indexedDB.programProgress?.length || 0,
    accessoryExercises: data.indexedDB.accessoryExercises?.length || 0,
    accessoryLogs: data.indexedDB.accessoryLogs?.length || 0,
    settings: data.localStorage?.sbs_settings ? true : false,
  };
}
